import React, { useReducer, Dispatch } from 'react';
import { ListingResponse } from '../Listings/Listings.types';
import cloneDeep from 'lodash/cloneDeep';

import { RawFilter } from '../../reducers/filters.types';
import { RawSortOrderOption } from '../../reducers/listingsSortOrder.types';
import { SeoLinks } from '../SeoLinks/SeoLinks.types';
import { DfpTargeting } from 'shared/components/Adverts/types';
import { coerceDfpAdTargetingObject } from 'shared/components/Adverts/lib/coerceDfpAdTargetingObject';
import { saveSearchAndAlertUrlFixer } from '../helpers/saveSearchAndAlertUrlFixer';

import { ActionAfterAuth } from 'shared/components/AuthModal/AuthModal.types';

export type Favourites = {
    listings: string[];
};

export type AnalyticsTaxonomy = {
    url: string;
    areaName: string;
    activity: string;
    brand: string;
    countryCode: string;
    countyAreaName: string;
    currencyCode: string;
    listingsCategory: string;
    outcode: string;
    outcodes?: string[];
    page: string;
    postalArea: string;
    priceMax?: string;
    priceMin?: string;
    radius: number;
    radiusAutoexpansion: string | null;
    regionName?: string;
    resultsSort: string;
    searchGuid: string;
    searchIdentifier: string;
    searchLocation: string;
    section: string;
    viewType: string;
    searchResultsCount: number;
    expandedResultsCount: number;
    totalResults: number;
    emailAllFormShown: boolean;
    emailAllTotalAgents: number;
    bedsMax: string | null;
    bedsMin: string | null;
    location: string | null;
    propertyType: string | null;
};

export type AnalyticsEcommerce = {
    currencyCode: string;
    impressions?: AnalyticsEcommerceImpression[];
};

export type AnalyticsEcommerceImpression = {
    id: number;
    list: string;
    position: number;
    variant: string;
};

export type ProductTrackingObject = {
    id: string;
    list: 'Featured listings';
    position: number;
    variant: string;
};

export type AdTargeting = {
    activity?: string | null;
    areaName?: string | null;
    brand?: string | null;
    brandPrimary?: string | null;
    countryCode?: string | null;
    currencyCode?: string | null;
    listingsCategory?: string | null;
    outcode?: string | null;
    outcodes?: string[] | null;
    page?: string | null;
    postalArea?: string | null;
    priceMax?: string | null;
    priceMin?: string | null;
    regionName?: string | null;
    resultsSort?: string | null;
    searchLocation?: string | null;
    searchResultsCount?: number | null;
    section?: string | null;
    totalResults?: number | null;
    url?: string | null;
    viewType?: string | null;
};

export type MetaInfo = {
    title: string;
    canonicalUri: string;
    description: string;
};

export type Pagination = {
    pageNumber: number;
    totalResults: number;
    totalResultsWasLimited: boolean;
};

export type Breadcrumb = {
    label: string | null;
    tracking?: string | null;
    uri?: string | null;
    i18nLabelKey?: string | null;
    noAppend?: number | null;
};
export type Links = {
    saveSearch: string | null;
    createAlert: string | null;
    mapView: string | null;
};

export type DiscoverMore = {
    housePricesUri: string | null;
    findAgentsUri: string | null;
};

export enum SearchType {
    ForSale = 'for-sale',
    ToRent = 'to-rent',
    NewHomes = 'new-homes'
}
export type SearchPageType = SearchType | 'for-sale' | 'to-rent' | 'new-homes';

export type SearchSource = 'refine' | 'facets' | '' | 'home';

export type SearchContextType = {
    dfpAdTargeting: DfpTargeting;
    seoAccordions: SeoLinks[];
    favourites: Favourites;
    links: Links;
    currentSearchSource: SearchSource;
    searchType: SearchPageType;
    searchCategory: SearchCategory;
    transactionType: SeoBlurb['transactionType'];
    pageTitle: string;
    showLocationValidation: boolean;
    userAlertId: string | null;
    savedSearchAndAlerts: SavedSearchAndAlerts | null;
    previousSavedSearchUri: string | null;
    isEditSavedSearchBannerVisible: boolean;
    isEditSavedSearchSuccess: boolean | null;
    actionAfterAuth: ActionAfterAuth | null;
};

export type Listings = {
    regular: ListingResponse[];
    extended: ListingResponse[];
    featured: ListingResponse[];
};

export type SearchCategory = 'commercial' | 'residential' | '';

export type SeoBlurb = {
    category: SearchCategory;
    transactionType: 'sale' | 'rent';
};

export type SearchResults = {
    analyticsTaxonomy: AnalyticsTaxonomy;
    analyticsEcommerce: AnalyticsEcommerce;
    adTargeting?: AdTargeting;
    metaInfo: MetaInfo;
    pagination: Pagination;
    filters: {
        fields: RawFilter[];
    };
    links: Links;
    sortOrder: {
        currentSortOrder: string;
        options: RawSortOrderOption[];
    };
    listings: Listings;
    seoBlurb: SeoBlurb;
    title: string;
    userAlertId: string | null;
    savedSearchAndAlerts: SavedSearchAndAlerts | null;
    polyenc: string | null;
    polygon?: number[][][] | null;
};

export type SavedSearchAndAlerts = {
    uri: string;
    currentFrequency: RawSortOrderOption;
};

export type Redirect = {
    code: number;
    redirectUri: string;
};

export type ServerData = {
    breadcrumbs: Breadcrumb[];
    seoAccordions: SeoLinks[];
    discoverMore: DiscoverMore;
    searchResults: SearchResults;
    redirects: Redirect[];
};

export type defaultContext = {
    state: SearchContextType;
    dispatch: Dispatch<Action>;
};

export const defaultState: SearchContextType = {
    favourites: {
        listings: []
    },
    dfpAdTargeting: {
        activity: '',
        area_name: '',
        brand: '',
        brand_primary: '',
        country_code: '',
        currency_code: '',
        device: '',
        listings_category: '',
        outcode: '',
        outcodes: [''],
        os: '',
        page: '',
        platform: '',
        postal_area: '',
        price_max: '',
        price_min: '',
        region_name: '',
        results_sort: '',
        search_location: '',
        search_results_count: '0',
        signed_in_status: '',
        section: '',
        total_results: '0',
        url: '',
        view_type: ''
    },
    seoAccordions: [],
    links: {
        saveSearch: '',
        createAlert: '',
        mapView: ''
    },
    currentSearchSource: '',
    searchType: SearchType.ForSale,
    searchCategory: '',
    transactionType: 'sale',
    pageTitle: '',
    showLocationValidation: false, // nothing is ever setting this
    userAlertId: null,
    savedSearchAndAlerts: null,
    previousSavedSearchUri: null,
    isEditSavedSearchBannerVisible: true,
    isEditSavedSearchSuccess: null,
    actionAfterAuth: null
};

export const generateInitialSearchState = (
    partial: Partial<SearchContextType> = {}
) => ({ ...defaultState, ...partial });

const defaultValue: defaultContext = {
    state: cloneDeep(defaultState),
    dispatch: () => ({})
};

export type Action =
    | { type: 'SET_DFP_AD_TARGETING'; payload: DfpTargeting }
    | { type: 'SET_SEO_LINKS'; payload: SeoLinks[] }
    | {
          type: 'SET_LISTINGS_USER_FAVOURITES';
          payload: {
              favourites: Favourites;
          };
      }
    | { type: 'ADD_FAVOURITE_LISTING'; payload: string }
    | { type: 'REMOVE_FAVOURITE_LISTING'; payload: string }
    | { type: 'SET_SEARCH_SOURCE'; payload: SearchSource }
    | { type: 'SET_USER_ALERT_ID'; payload: string | null }
    | {
          type: 'SET_SAVED_SEARCH_AND_ALERTS';
          payload: SavedSearchAndAlerts | null;
      }
    | { type: 'SET_PREVIOUS_SAVED_SEARCH_URI'; payload: string | null }
    | { type: 'SET_ACTION_AFTER_AUTH'; payload: ActionAfterAuth | null }
    | { type: 'SET_IS_EDIT_SAVED_SEARCH_BANNER_VISIBLE'; payload: boolean }
    | { type: 'SET_IS_EDIT_SAVED_SEARCH_SUCCESS'; payload: boolean | null };

export const searchReducer = (
    state: SearchContextType,
    action: Action
): SearchContextType => {
    switch (action.type) {
        case 'SET_DFP_AD_TARGETING':
            return { ...state, dfpAdTargeting: action.payload };
        case 'SET_SEO_LINKS':
            return { ...state, seoAccordions: action.payload };
        case 'SET_LISTINGS_USER_FAVOURITES':
            return {
                ...state,
                favourites: action.payload.favourites
            };
        case 'ADD_FAVOURITE_LISTING':
            return {
                ...state,
                favourites: {
                    listings: [...state.favourites?.listings].concat([
                        action.payload
                    ])
                }
            };
        case 'REMOVE_FAVOURITE_LISTING':
            return {
                ...state,
                favourites: {
                    listings: [...state.favourites?.listings].filter(
                        (favouriteId) => favouriteId !== action.payload
                    )
                }
            };
        case 'SET_SEARCH_SOURCE':
            return {
                ...state,
                currentSearchSource: action.payload
            };

        case 'SET_USER_ALERT_ID':
            return {
                ...state,
                userAlertId: action.payload
            };
        case 'SET_SAVED_SEARCH_AND_ALERTS':
            return {
                ...state,
                savedSearchAndAlerts: action.payload
                    ? {
                          uri: saveSearchAndAlertUrlFixer(
                              action.payload.uri
                          ) as string,
                          currentFrequency: action.payload.currentFrequency
                      }
                    : null
            };
        case 'SET_PREVIOUS_SAVED_SEARCH_URI':
            return {
                ...state,
                previousSavedSearchUri: action.payload
            };
        case 'SET_ACTION_AFTER_AUTH':
            return {
                ...state,
                actionAfterAuth: action.payload
            };
        case 'SET_IS_EDIT_SAVED_SEARCH_BANNER_VISIBLE':
            return {
                ...state,
                isEditSavedSearchBannerVisible: action.payload
            };
        case 'SET_IS_EDIT_SAVED_SEARCH_SUCCESS':
            return {
                ...state,
                isEditSavedSearchSuccess: action.payload
            };
    }
};

export const SearchContext = React.createContext(defaultValue);
SearchContext.displayName = 'SearchContext';

export const mapDataToActions = (serverData: ServerData): Action[] => {
    const actions: Action[] = [];
    actions.push({
        type: 'SET_DFP_AD_TARGETING',
        payload: coerceDfpAdTargetingObject(
            serverData?.searchResults?.adTargeting
        )
    });
    actions.push({
        type: 'SET_SEO_LINKS',
        payload: serverData?.seoAccordions
    });
    actions.push({
        type: 'SET_USER_ALERT_ID',
        payload: serverData?.searchResults?.userAlertId
    });
    actions.push({
        type: 'SET_SAVED_SEARCH_AND_ALERTS',
        payload: serverData?.searchResults?.savedSearchAndAlerts
    });

    if (serverData?.searchResults?.userAlertId === null) {
        actions.push({
            type: 'SET_PREVIOUS_SAVED_SEARCH_URI',
            payload: null
        });
    }

    if (!serverData?.searchResults?.savedSearchAndAlerts) {
        actions.push({
            type: 'SET_IS_EDIT_SAVED_SEARCH_BANNER_VISIBLE',
            payload: true
        });

        actions.push({
            type: 'SET_IS_EDIT_SAVED_SEARCH_SUCCESS',
            payload: null
        });
    }

    return actions;
};

type Props = {
    initialState?: SearchContextType;
};

export const SearchProvider: React.FC<Props> = ({ children, initialState }) => {
    const [state, dispatch] = useReducer(
        searchReducer,
        initialState || defaultState
    );

    return (
        <SearchContext.Provider value={{ state, dispatch }}>
            {children}
        </SearchContext.Provider>
    );
};
